import { Typography, Button, Skeleton, Card, Col, Row } from 'antd';
import { ShoppingCartOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useProducts, Product, useAuth } from '@hooks';
import './styles.css';

const { Title, Paragraph } = Typography;

// Estilos personalizados para parágrafos
const paragraphStyle = {
  fontFamily: 'Barlow, sans-serif',
  fontSize: '1.125rem',
  lineHeight: '1.8',
};

export const ProductsPage = () => {
  const navigate = useNavigate();
  const { products } = useProducts();
  const { profile } = useAuth();
  
  const isAdmin = profile.data?.profile === 'admin';

  const getProductImage = (product: Product) => {
    if (product.imageUrl) {
      // Check if imageUrl is already a full URL (starts with http)
      if (product.imageUrl.startsWith('http')) {
        return product.imageUrl;
      }
      // Otherwise, construct the S3 URL using environment variables
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }
    return `https://placehold.co/400x500?text=${encodeURIComponent(product.name)}`;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="w-full py-20 bg-zinc-900">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-between items-center mb-8">
            <div className="flex-1">
              <Title level={1} className="!text-white mb-0 font-android">Modlink Store</Title>
            </div>
            {isAdmin && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => navigate('/products')}
                className="btn-orangicide text-black border-none font-android"
                size="large"
              >
                Manage Products
              </Button>
            )}
          </div>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="text-gray-300 text-lg" style={paragraphStyle}>
              Discover our exclusive collection of products. High quality items designed for you.
            </Paragraph>
          </div>
        </div>
      </section>
      
      {/* Products Section */}
      <section className="w-full py-20 bg-black">
        <div className="container mx-auto px-4">
          <Title level={2} className="!text-white text-center mb-16 font-android">Our Products</Title>
          
          {products.isLoading && (
            <Row gutter={[32, 48]}>
              {[1, 2, 3].map((i) => (
                <Col xs={24} sm={12} md={8} key={i}>
                  <div className="bg-zinc-900 p-6">
                    <Skeleton active paragraph={{ rows: 4 }} />
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {products.isError && (
            <div className="text-center py-8">
              <Title level={4} className="!text-white">
                ERROR LOADING PRODUCTS
              </Title>
              <Paragraph className="text-gray-300 mb-4">
                We couldn't load the products. Please try again.
              </Paragraph>
              <Button
                onClick={() => products.refetch()}
                className="btn-orangicide !text-black"
              >
                TRY AGAIN
              </Button>
            </div>
          )}

          {!products.isLoading && !products.isError && (
            <>
              {products.data && products.data.length > 0 ? (
                <Row gutter={[32, 48]}>
                  {products.data.map((product: Product) => (
                    <Col xs={24} sm={12} md={8} key={product.id}>
                      <Card 
                        className="bg-zinc-900 border-0 overflow-hidden hover:shadow-lg hover:shadow-orangicide/20 transition-all duration-300"
                        bodyStyle={{ padding: 0 }}
                        cover={
                          <div className="h-80 overflow-hidden">
                            <img 
                              src={getProductImage(product)}
                              alt={product.name} 
                              className="w-full h-full object-scale-down object-center"
                              onError={(e) => {
                                e.currentTarget.src = `https://placehold.co/400x500?text=${encodeURIComponent(product.name)}`;
                              }}
                            />
                          </div>
                        }
                      >
                        <div className="p-6">
                          <Title level={4} className="!text-white mb-2 font-android">{product.name}</Title>
                          <div className="text-orangicide font-bold text-xl mb-4">
                            {(() => {
                              const basePrice = product.variants && product.variants.length > 0
                                ? Math.max(...product.variants.map(v => Number(v.price) || 0))
                                : 0;
                              return formatPrice(basePrice);
                            })()}
                          </div>
                          <Paragraph 
                            className="text-gray-300 mb-4 line-clamp-3" 
                            style={paragraphStyle}
                          >
                            {product.description}
                          </Paragraph>
                          <Button 
                            type="primary" 
                            className="btn-orangicide text-black border-none w-full"
                            icon={<ShoppingCartOutlined />}
                          >
                            View Details
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center py-12">
                  <Title level={4} className="!text-white">
                    NO PRODUCTS AVAILABLE
                  </Title>
                  <Paragraph className="text-gray-300 mb-6">
                    {isAdmin ? 'Start by adding your first product to the catalog.' : 'Check back soon for new products!'}
                  </Paragraph>
                  {isAdmin && (
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => navigate('/products')}
                      className="btn-orangicide text-black border-none font-android"
                    >
                      Add Products
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="w-full py-16 bg-orangicide text-black">
        <div className="container mx-auto px-4 text-center">
          <Title level={2} className="!text-black mb-6">Need Help?</Title>
          <Paragraph className="text-xl mb-8">
            Have questions about our products? Get in touch with us.
          </Paragraph>
          <Button 
            size="large" 
            onClick={() => navigate('/contact')}
            className="min-w-[150px] bg-black text-white hover:bg-zinc-800 border-none"
          >
            Contact Us
          </Button>
        </div>
      </section>
    </div>
  );
};

